/**
 * API Utility
 *
 * Provides authenticated fetch wrapper that automatically adds JWT tokens
 * and handles token refresh on 401 errors.
 */

import { API_BASE_URL } from '../config';

let authContextRef = null;

// Set the auth context reference (called from App.js)
export const setAuthContext = (authContext) => {
  authContextRef = authContext;
};

/**
 * Authenticated fetch wrapper
 *
 * Automatically adds JWT token to requests and handles token refresh
 *
 * @param {string} endpoint - API endpoint (e.g., '/employees' or 'employees')
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
  // Normalize endpoint (remove leading slash if present)
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const url = `${API_BASE_URL}/${normalizedEndpoint}`;

  // Get access token from auth context
  let token = null;
  if (authContextRef && authContextRef.getAccessToken) {
    try {
      token = await authContextRef.getAccessToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      // If token refresh fails, auth context will handle logout
      throw error;
    }
  }

  // Add authorization header if token exists
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Important for refresh token cookies
  });

  // Handle 401 Unauthorized - try to refresh token
  if (response.status === 401 && authContextRef) {
    try {
      // Try to refresh the token
      const newToken = await authContextRef.refreshAccessToken();

      // Retry the request with new token
      headers['Authorization'] = `Bearer ${newToken}`;
      const retryResponse = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      return retryResponse;
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // Auth context will handle logout
      throw refreshError;
    }
  }

  return response;
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (endpoint) => {
  const response = await apiFetch(endpoint, { method: 'GET' });
  return response.json();
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (endpoint, data) => {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (endpoint, data) => {
  const response = await apiFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (endpoint) => {
  const response = await apiFetch(endpoint, { method: 'DELETE' });
  return response.json();
};
